import React from 'react';
import { Copy, MessageCircle, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useTranslation } from '../../hooks/useTranslation';

// REGLA MÁS IMPORTANTE DE ESTE COMPONENTE — "cero envío automático":
// el botón de WhatsApp abre wa.me/{telefono}?text={mensaje} en pestaña
// nueva (el usuario pulsa enviar DENTRO de WhatsApp, con su propia
// cuenta). El de email abre mailto:. NINGÚN botón dispara una petición
// al backend para enviar nada — ni siquiera como placeholder "para más
// adelante". El backend solo GENERA el texto; enviarlo es siempre un
// acto manual de Marta desde su propia app de WhatsApp/su propio email.
export function MessageCard({
  title,
  body,
  subject,
  phone,
  email,
  channel, // 'whatsapp' | 'email' | null (call_script/internal_notes no tienen canal de envío)
  wordCount,
  exceedsWordLimit,
  messageType, // clave real para POST /leads/{id}/mark-message-copied
  onCopy,
}) {
  const { t } = useTranslation();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success(t('aiLeadScanner.messageCard.copied'));
    } catch {
      toast.error(t('aiLeadScanner.messageCard.copyError'));
    }
    // Un solo clic hace las dos cosas: copia Y registra el evento
    // MESSAGE_COPIED (Commit 13) — no cambia el status del lead.
    onCopy?.(messageType);
  };

  const handleWhatsApp = () => {
    const target = phone?.replace(/[^\d+]/g, '') || '';
    const url = `https://wa.me/${target.replace('+', '')}?text=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleEmail = () => {
    // encodeURIComponent() directo, NO URLSearchParams — URLSearchParams
    // codifica espacios como "+" (form-urlencoded), pero RFC 6068 (URI
    // mailto:) exige percent-encoding real ("%20"). Con "+" algunos
    // clientes de correo pueden mostrar signos "+" literales en vez de
    // espacios en el asunto/cuerpo. Verificado con un test real: ver nota
    // de trazabilidad en el mensaje de commit de esta pieza.
    const parts = [];
    if (subject) parts.push(`subject=${encodeURIComponent(subject)}`);
    parts.push(`body=${encodeURIComponent(body)}`);
    window.open(`mailto:${email || ''}?${parts.join('&')}`, '_blank');
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-zinc-200">{title}</h3>
        {typeof wordCount === 'number' && (
          <span className="text-xs text-zinc-500">{wordCount} {t('aiLeadScanner.messageCard.words')}</span>
        )}
      </div>

      {exceedsWordLimit && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-400">{t('aiLeadScanner.messageCard.exceedsLimit')}</p>
        </div>
      )}

      {subject && (
        <p className="text-xs text-zinc-500 mb-1">
          <span className="font-medium text-zinc-400">{t('aiLeadScanner.messageCard.subject')}:</span> {subject}
        </p>
      )}

      <p className="text-sm text-zinc-300 whitespace-pre-wrap mb-3">{body}</p>

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={handleCopy} data-testid={`copy-${messageType}`}>
          <Copy className="w-3.5 h-3.5 mr-1.5" />
          {t('aiLeadScanner.messageCard.copy')}
        </Button>
        {channel === 'whatsapp' && phone && (
          <Button size="sm" variant="outline" onClick={handleWhatsApp} data-testid={`whatsapp-${messageType}`}>
            <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
            WhatsApp
          </Button>
        )}
        {channel === 'email' && (
          <Button size="sm" variant="outline" onClick={handleEmail} data-testid={`email-${messageType}`}>
            <Mail className="w-3.5 h-3.5 mr-1.5" />
            Email
          </Button>
        )}
      </div>
    </div>
  );
}
